import { CLICK_IDS } from '@sunflower-games/analytics-events';
import { action, observable } from 'mobx';

import {
  MonetizationEventsEventType,
  type MonetizationEventsCurrentEvent,
  type MonetizationEventsResponse,
} from '../../dto/monetization-events.types';
import { analytics } from '../../services/analytics';
import { getRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { devError } from '../../utils/log';
import type { FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import { METAGAME_EVENTS_DIALOG_TYPES } from './metagame-events/constants';
import { SPORTS_DIALOG_TYPES } from './sport-events/constants';

export class MonetizationEventsStore extends Loadable implements IFeatureManagerStore {
  protected readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;

    const monetizationEventsEnabled = resolveFlag('monetization_events_enabled', true);
    this.monetizationEventsFlag = monetizationEventsEnabled;

    const crownSportsFeature = resolveFlag('crown_sports_feature', false);
    this.crownSportsFeatureFlag = crownSportsFeature;

    ldClient?.on('ready', () => {
      this.monetizationEventsFlag = resolveFlag('monetization_events_enabled', true);
      this.crownSportsFeatureFlag = resolveFlag('crown_sports_feature', false);
    });

    ldClient?.on('change', (changes) => {
      if ('monetization_events_enabled' in changes) {
        this.monetizationEventsFlag = resolveFlag('monetization_events_enabled', true);
      }

      if ('crown_sports_feature' in changes) {
        this.crownSportsFeatureFlag = resolveFlag('crown_sports_feature', false);
      }
    });
  }

  @observable
  private monetizationEventsFlag: boolean = false;

  @action
  public setMonetizationEventsFlag(flag: boolean) {
    this.monetizationEventsFlag = flag;
  }

  @observable
  private crownSportsFeatureFlag: boolean = false;

  @action
  public setCrownSportsFeatureFlag(flag: boolean) {
    this.crownSportsFeatureFlag = flag;
  }

  @observable
  public onMonetizationEventsDialogClose: VoidFunction | undefined = undefined;

  public currentOpenSportEvent: MonetizationEventsCurrentEvent | undefined = undefined;

  public setCurrentOpenSportEvent(event: MonetizationEventsCurrentEvent | undefined) {
    this.currentOpenSportEvent = event;
  }

  private sportDialogTypes: FeatureManagerDialogType[] = [...Object.values(SPORTS_DIALOG_TYPES)];

  private metagameDialogTypes: FeatureManagerDialogType[] = [...Object.values(METAGAME_EVENTS_DIALOG_TYPES)];

  public dialogTypes: FeatureManagerDialogType[] = [...this.sportDialogTypes];

  public async fetch() {
    if (!this.monetizationEventsFlag) return 'OFF';

    if (this.loading) return 'FETCHING';

    this.setLoading(true);

    try {
      const response = await getRequest<{ data: MonetizationEventsResponse }>('monetization-events/user');
      const { monetizationEvents } = response.data;

      const sportEvent = monetizationEvents.find(
        (monetizationEvent) => monetizationEvent.providerType === MonetizationEventsEventType.SPORTS,
      );

      if (sportEvent && this.crownSportsFeatureFlag) {
        this.rootStore.sportEventsStore.setSportEvent(sportEvent);
      }

      return 'READY';
    } catch (error) {
      devError(':MonetizationEventsStore: Error fetching monetization events', error);
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  }

  startDialogs(type?: FeatureManagerDialogType | undefined) {
    if (type && this.sportDialogTypes.includes(type)) {
      return this.rootStore.sportEventsStore.startDialogs(type);
    }

    if (type && this.metagameDialogTypes.includes(type)) {
      return this.rootStore.metagamesStore.startDialogs(type);
    }

    return false;
  }

  public sendAnalyticsEvent({
    event_action,
    triggered_by,
    feature_sub_type,
    click_location = 'widget',
  }: {
    event_action: 'click' | 'impression';
    triggered_by?: 'click' | 'lobby';
    feature_sub_type: 'game_of_the_day' | 'toto';
    click_location: 'widget' | 'in_game_widget' | 'close';
  }) {
    const { coinType } = this.rootStore.coinTypeStore;
    const lobby_type = coinType === 'GC' ? 'GC' : 'CC';

    if (event_action === 'click') {
      void analytics.sendEvent('widget', {
        feature_type: 'metagames',
        feature_sub_type: feature_sub_type as any,
        event_action,
        lobby_type,
        click_id: CLICK_IDS.LOBBY_PAGE.WIDGET.METAGAMES,
        click_location,
      });
    } else if (event_action === 'impression') {
      void analytics.sendEvent('widget', {
        feature_type: 'metagames',
        feature_sub_type: feature_sub_type as any,
        event_action,
        triggered_by: triggered_by || 'click',
        lobby_type,
      });
    }
  }
}
